import { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { format } from "fecha";
import { makeStyles } from "@mui/styles";
import { Button, Grid } from "@mui/material";

import { putOrder, putPoEmail } from "../../../services/OrderService";

import JobOrdersTable from "../JobOrdersTable";
import MultipleOrdersDeliveryDateSetModal from "../../../components/Modal/MultipleOrdersDeliveryDateSetModal";
import ViewPOSummaryModal from "../../../components/Modal/ViewPOSummaryModal";
import AppContext from "../../../context/AppContext";

const useStyles = makeStyles({
  root: {},
});

const OrderDetail = ({ loading, data, handleUpdate }) => {
  const classes = useStyles();
  const { user } = useContext(AppContext);
  const [jobOrders, setJobOrders] = useState([]);
  const [deliveryDateFormData, setDeliveryDateFormData] = useState({
    orders: [],
    deliveryDate: null,
  });
  const [isViewPOSummaryModalOpen, setIsViewPOSummaryModalOpen] =
    useState(false);
  const [isDeliveryDateModalOpen, setIsDeliveryDateModalOpen] = useState(false);
  const [sendingPreliminaryPORequest, setSendingPreliminaryPORequest] =
    useState(false);
  const [submittingOrdersDeliveryDate, setSubmittingOrdersDeliveryDate] =
    useState(false);

  const isSendPreliminaryPOButtonEnabled = useMemo(() => {
    let numTotalItemsSent = 0;
    let numTotalItems = 0;

    jobOrders.forEach((item) => {
      const materialsItems = item.ORDER_ITEMS
        ? JSON.parse(item.ORDER_ITEMS)
        : [];
      const numSent = materialsItems.filter(
        (mItem) => mItem.PRELIMINARY_PO_REQUEST_SENT
      ).length;

      numTotalItemsSent += numSent;
      numTotalItems += materialsItems.length;
    });

    return numTotalItemsSent !== numTotalItems;
  }, [jobOrders]);

  useEffect(() => {
    setJobOrders(data.ORDERS);
  }, [data]);

  const handleDeliveryDateSet = async (formData) => {
    const promises = formData.orders.map((item) =>
      putOrder({
        ...item,
        PO_CREATE_DATE: format(
          new Date(formData.deliveryDate),
          "YYYY-MM-DD hh:mm:ss"
        ),
        ORDER_TIME: format(new Date(), "YYYY-MM-DD hh:mm:ss"),
      })
    );

    setSubmittingOrdersDeliveryDate(true);

    try {
      const res = await Promise.all(promises);

      if (res[0] && res[0].status === 200) {
        toast.success("Delivery date has been updated!");
        setSubmittingOrdersDeliveryDate(false);
        setIsDeliveryDateModalOpen(false);
        setDeliveryDateFormData({ orders: [], deliveryDate: null });
        handleUpdate();
      } else {
        setSubmittingOrdersDeliveryDate(false);
        toast.error(
          "Unexpected error occures while updating the delivery dates!"
        );
      }
    } catch (err) {
      console.error(err);
      setSubmittingOrdersDeliveryDate(false);
      toast.error(err);
    }
  };

  const handlePreliminaryPORequestSend = () => {
    const payload = jobOrders.map((order) => ({
      ORDER_ID: order.ORDER_ID,
      PRELIMINARY_PO_REQUEST_SENT: 1,
    }));

    setSendingPreliminaryPORequest(true);

    putPoEmail(payload)
      .then(async (res) => {
        if (res.status === 200) {
          setSendingPreliminaryPORequest(false);
          toast.success(`Preliminary PO Request email has been sent!`);
          handleUpdate();
        } else {
          setSendingPreliminaryPORequest(false);
          toast.error(
            "Unexpected error occurs while sending the preliminary PO request!"
          );
        }
      })
      .catch((err) => {
        setSendingPreliminaryPORequest(false);
        console.log(err);
        toast.error(`${err}`);
      });
  };

  const handleViewPOSummaryModalOpen = () => {
    setIsViewPOSummaryModalOpen(true);
  };
  const handleViewPOSummaryModalClose = () => {
    setIsViewPOSummaryModalOpen(false);
  };

  const handleDeliveryDateModalOpen = () => {
    setIsDeliveryDateModalOpen(true);
  };
  const handleDeliveryDateModalClose = () => {
    setIsDeliveryDateModalOpen(false);
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Grid
            container
            alignItems="center"
            justifyContent="flex-end"
            spacing={2}
          >
            <Grid item>
              <Button
                variant="contained"
                onClick={handleViewPOSummaryModalOpen}
                disabled={!jobOrders.length}
                fullWidth
              >
                View PO Summary
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                onClick={handlePreliminaryPORequestSend}
                disabled={
                  !jobOrders.length ||
                  !isSendPreliminaryPOButtonEnabled ||
                  sendingPreliminaryPORequest
                }
                fullWidth
              >
                Send Preliminary PO
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                onClick={handleDeliveryDateModalOpen}
                disabled={!jobOrders.length}
                fullWidth
              >
                Set Delivery Date
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <JobOrdersTable
            title="Orders"
            loading={loading}
            items={jobOrders}
            expandable={false}
            hiddenColumns={
              user?.USER_ROLE === "FOREMAN"
                ? ["FOREMAN", "PO_TOTAL"]
                : ["FOREMAN"]
            }
            handleDeliveryDateSet={handleDeliveryDateSet}
          />
        </Grid>
        <Grid item>
          <ViewPOSummaryModal
            open={isViewPOSummaryModalOpen}
            loading={loading}
            job={data}
            unit={data}
            orders={jobOrders}
            handleClose={handleViewPOSummaryModalClose}
          />
        </Grid>
        <Grid item>
          <MultipleOrdersDeliveryDateSetModal
            open={isDeliveryDateModalOpen}
            loading={loading || submittingOrdersDeliveryDate}
            orders={jobOrders}
            formData={deliveryDateFormData}
            handleSave={handleDeliveryDateSet}
            handleClose={handleDeliveryDateModalClose}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default OrderDetail;
